import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import { collection, addDoc, updateDoc, query, where, getDocs, doc } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { ApplyJobPayloadInterface } from "./dto";
import { APPLICATION_STATUS } from "enum/job-status-type";
import { QueryKeys } from "config/query-keys";

// --- ADDITION: Ensure this interface in your dto.ts file includes the new field ---
// export interface ApplyJobPayloadInterface {
//   id: string;
//   data: {
//     coverLetter?: string;
//     resume: string;
//     questionAnswers: Record<string, string | boolean>;
//     totalExperienceYears: number; // This line must be added
//   };
//   enumerator_info: any;
// }

export const applyJobs = async ({ id, data, enumerator_info }: ApplyJobPayloadInterface) => {
  if (!id || !enumerator_info) {
    const errorMessage = "Invalid data: Job ID or enumerator_info is missing.";
    console.error("Error in applyJobs:", errorMessage);
    throw new Error(errorMessage);
  }

  try {
    const jobApplicationCollectionRef = collection(db, "applications");
    
    const existingQuery = query(
      jobApplicationCollectionRef,
      where("jobId", "==", id),
      where("applicant_info", "==", enumerator_info)
    );

    const querySnapshot = await getDocs(existingQuery);

    if (!querySnapshot.empty) {
      const existingDoc = querySnapshot.docs[0];
      const applicationRef = doc(db, "applications", existingDoc.id);

      await updateDoc(applicationRef, {
        resume: data.resume || existingDoc.data().resume,
        coverLetter: data.coverLetter || existingDoc.data().coverLetter,
        questionAnswers: data.questionAnswers || existingDoc.data().questions || {},
        totalExperienceYears: data.totalExperienceYears, // --- ADDITION ---
        status: APPLICATION_STATUS.SUBMITTED,
        appliedAt: new Date(),
        applicant_info: enumerator_info,
      });

      return { success: true, updated: true };
    }

    const applicationRef = await addDoc(jobApplicationCollectionRef, {
      jobId: id,
      applicant_info: enumerator_info,
      resume: data.resume || "",
      coverLetter: data.coverLetter || "",
      questionAnswers: data.questionAnswers || {},
      totalExperienceYears: data.totalExperienceYears, // --- ADDITION ---
      status: APPLICATION_STATUS.SUBMITTED,
      appliedAt: new Date(),
    });

    await updateDoc(applicationRef, { id: applicationRef.id });

    return { success: true, created: true };
  } catch (error) {
    console.error("Error applying for job in Firestore:", error);
    throw error;
  }
};

export const useApplyJobs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: applyJobs,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.JOBS] });
    },
    onError: (error) => {
      notification.error({
        message: "Application Failed",
        description: error instanceof Error ? error.message : "There was an issue applying for the job.",
      });
    },
  });
};